// Peças de leitura de dado: número em destaque, etiqueta de estado, barra de
// proporção, tabela, vazio e carregando.
//
// O painel administra bastante coisa e mostrava quase tudo como texto
// corrido. Número que exige ação precisa ter peso de número, e estado
// precisa ter cor, senão a leitura vira procura.

import {
  TEXTO, CINZA_ESC, BRANCO, AZUL, BORDA, RAIO, ESP, TIPO,
  FUNDO_SUAVE, NEUTRO_CL, OK, OK_CL, ATENCAO, ATENCAO_CL, RUIM, RUIM_CL,
} from "../tema";
import Icone, { IconeOrdenar } from "./Icone";

// ── Número em destaque ──────────────────────────────────────
// Um número, o que ele é, e no máximo uma linha de contexto. A tentação é
// encher de detalhe; o cartão existe justamente para ser lido de longe.
export function Estatistica({ valor, rotulo, contexto, cor = AZUL, acao }) {
  return (
    <div style={{
      background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
      padding: "14px 16px", display: "flex", flexDirection: "column", gap: 2,
      cursor: acao ? "pointer" : "default", minWidth: 0,
    }} onClick={acao}>
      <div style={{ ...TIPO.numero, color: cor, lineHeight: 1.1 }}>{valor}</div>
      <div style={{ fontSize: 12, color: CINZA_ESC, fontWeight: 600 }}>{rotulo}</div>
      {contexto && (
        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 2, lineHeight: 1.45 }}>{contexto}</div>
      )}
    </div>
  );
}

// Fileira de estatísticas que se reorganiza sozinha conforme a largura.
export function FileiraDeEstatisticas({ children, minimo = 150 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${minimo}px, 1fr))`,
      gap: ESP.md,
    }}>
      {children}
    </div>
  );
}

// ── Etiqueta de estado ──────────────────────────────────────
const TONS = {
  neutro: { bg: NEUTRO_CL, cor: CINZA_ESC },
  info: { bg: "#DBEAFE", cor: "#1E40AF" },
  ok: { bg: OK_CL, cor: OK },
  atencao: { bg: ATENCAO_CL, cor: ATENCAO },
  ruim: { bg: RUIM_CL, cor: RUIM },
};

// `ponto` acende uma bolinha antes do texto. Serve para a etiqueta que diz
// uma SITUAÇÃO ("Ativo", "Pendente"), que é a que se procura correndo o
// olho pela coluna; a etiqueta que só classifica ("Coletiva", "BMX") fica
// sem, senão a tela inteira vira um mostruário de bolinhas.
export function Etiqueta({ children, tom = "neutro", ponto = false, style = {} }) {
  const t = TONS[tom] || TONS.neutro;
  return (
    <span style={{
      background: t.bg, color: t.cor, borderRadius: RAIO.pilula,
      padding: ponto ? "4px 11px 4px 9px" : "3px 10px",
      fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 6,
      ...style,
    }}>
      {ponto && (
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: "currentColor", flexShrink: 0, opacity: 0.85,
        }} />
      )}
      {children}
    </span>
  );
}

// ── Barra de proporção ──────────────────────────────────────
// Percentual acompanhado da barra. O número diz o valor exato, a barra diz
// se está perto ou longe, e é a segunda leitura que se faz correndo o olho
// por uma lista de trinta atletas.
export function Barra({ pct, cor = AZUL, altura = 6, largura = "100%" }) {
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ background: NEUTRO_CL, borderRadius: RAIO.pilula, height: altura, width: largura, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: cor, borderRadius: RAIO.pilula }} />
    </div>
  );
}

// ── Tabela ──────────────────────────────────────────────────
// Cabeçalho fixo, linhas alternadas e dígitos alinhados em coluna. Rola
// dentro da própria caixa, para a página nunca rolar de lado.
// Uma coluna pode ser o rótulo sozinho ("Nome") ou um objeto
// { rotulo, alinha, chave }. Com `chave` e `onOrdenar`, o cabeçalho vira
// botão e mostra as duas pontas de seta: a acesa diz por onde a lista está
// ordenada agora, o que antes não aparecia em lugar nenhum.
export function Tabela({ colunas, children, ordenarPor, direcao, onOrdenar, style = {} }) {
  return (
    <div style={{ overflowX: "auto", ...style }}>
      <table style={{
        borderCollapse: "collapse", width: "100%", fontSize: 13,
        fontVariantNumeric: "tabular-nums",
      }}>
        <thead>
          <tr style={{ background: "#F1F5F9" }}>
            {colunas.map((c, i) => {
              const rotulo = typeof c === "string" ? c : c.rotulo;
              const alinha = typeof c === "string" ? (i === 0 ? "left" : "center") : (c.alinha || "center");
              const chave = typeof c === "string" ? null : c.chave;
              const podeOrdenar = Boolean(chave && onOrdenar);
              const conteudo = podeOrdenar ? (
                <button
                  type="button"
                  onClick={() => onOrdenar(chave)}
                  title={"Ordenar por " + rotulo}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "none", border: "none", padding: 0, cursor: "pointer",
                    font: "inherit", color: "inherit", fontWeight: 700,
                    flexDirection: alinha === "right" ? "row-reverse" : "row",
                  }}
                >
                  {rotulo}
                  <IconeOrdenar direcao={ordenarPor === chave ? direcao : null} />
                </button>
              ) : rotulo;
              return (
                <th key={String(rotulo) + i} style={{
                  textAlign: alinha, padding: "12px 12px", color: "#334155",
                  fontWeight: 700, fontSize: 12.5, whiteSpace: "nowrap",
                  borderBottom: "1px solid " + BORDA,
                }}>
                  {conteudo}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// Ordenação de lista.
//
// Mora aqui porque cada tela que ordena repetia a mesma função de
// comparar, e as diferenças entre as cópias apareciam no uso: uma tratava
// acento, outra não; uma alternava a direção ao clicar de novo, outra
// sempre voltava para crescente.
export function ordenarLista(lista, chave, direcao, valorDe) {
  if (!chave) return lista;
  const sinal = direcao === "desc" ? -1 : 1;
  const pegar = valorDe || ((item) => item[chave]);
  return [...lista].sort((a, b) => {
    const va = pegar(a, chave), vb = pegar(b, chave);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sinal;
    return String(va == null ? "" : va).localeCompare(
      String(vb == null ? "" : vb), "pt-BR", { numeric: true, sensitivity: "base" }
    ) * sinal;
  });
}

// ── Paginação ───────────────────────────────────────────────
// Anterior, a página atual e próxima. Sem fila de números: as telas do
// painel filtram antes de paginar, e quem precisa da página sete vai mudar
// o filtro, não contar páginas.
export function Paginacao({ pagina, totalPaginas, onIr }) {
  if (!totalPaginas || totalPaginas < 1) return null;
  const botao = (habilitado) => ({
    width: 34, height: 34, borderRadius: RAIO.sm,
    border: "1px solid " + BORDA, background: BRANCO,
    color: habilitado ? TEXTO : "#CBD5E1",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: habilitado ? "pointer" : "not-allowed", padding: 0,
  });
  return (
    <>
      <button type="button" aria-label="Página anterior" disabled={pagina <= 1}
        onClick={() => onIr(pagina - 1)} style={botao(pagina > 1)}>
        <Icone nome="chevronEsquerda" tamanho={15} />
      </button>
      <span style={{
        minWidth: 34, height: 34, borderRadius: RAIO.sm, background: AZUL, color: BRANCO,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 700, padding: "0 8px", boxSizing: "border-box",
      }}>{pagina}</span>
      <button type="button" aria-label="Próxima página" disabled={pagina >= totalPaginas}
        onClick={() => onIr(pagina + 1)} style={botao(pagina < totalPaginas)}>
        <Icone nome="chevronDireita" tamanho={15} />
      </button>
      <span style={{ fontSize: 12.5, color: CINZA_ESC, marginLeft: 4 }}>
        Página {pagina} de {totalPaginas}
      </span>
    </>
  );
}

// A linha separa-se da seguinte por um fio, e não por faixa cinza
// alternada. Zebra continua disponível para as grades de presença, onde a
// linha tem trinta colunas e o olho precisa da faixa para não se perder;
// numa lista de cinco colunas ela só suja.
export function Linha({ children, destaque = false, zebra = false }) {
  return (
    <tr style={{
      background: destaque ? "#EEF2F7" : zebra ? FUNDO_SUAVE : BRANCO,
      fontWeight: destaque ? 800 : 400,
      color: destaque ? "#24417D" : TEXTO,
      borderBottom: "1px solid #EFF3F8",
    }}>
      {children}
    </tr>
  );
}

export function Celula({ children, alinha = "center", style = {} }) {
  return (
    <td style={{ padding: "12px 12px", textAlign: alinha, ...style }}>{children}</td>
  );
}

// ── Estados ─────────────────────────────────────────────────
// Vazio explica por que está vazio e o que fazer. "Nenhum registro" sozinho
// deixa quem lê sem saber se o sistema falhou ou se não há mesmo nada.
export function Vazio({ icone = "—", titulo, children }) {
  return (
    <div style={{
      padding: "26px 20px", background: FUNDO_SUAVE, border: `1px dashed ${BORDA}`,
      borderRadius: RAIO.md, textAlign: "center",
    }}>
      <div style={{ fontSize: 22, lineHeight: 1, marginBottom: ESP.sm }}>{icone}</div>
      {titulo && <div style={{ fontSize: 14, fontWeight: 700, color: TEXTO }}>{titulo}</div>}
      {children && (
        <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 5, lineHeight: 1.6, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Esqueleto do conteúdo enquanto carrega, em vez da palavra "carregando".
// Mostra desde o primeiro instante o formato do que vem, e a tela não dá o
// salto que dá quando o texto é substituído por uma tabela inteira.
export function Carregando({ linhas = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ESP.sm }}>
      {Array.from({ length: linhas }).map((_, i) => (
        <div key={i} style={{
          height: 14, borderRadius: RAIO.sm, background: NEUTRO_CL,
          width: i === 0 ? "38%" : i % 2 ? "100%" : "82%",
          opacity: 0.9,
        }} />
      ))}
    </div>
  );
}
